import React, { useEffect, useState, useRef } from 'react';

export default function IntroSequence({ onComplete }) {
  const [showTitle, setShowTitle] = useState(false);
  const [isMusicMuted, setIsMusicMuted] = useState(false);
  const videoRef = useRef(null);
  const welcomeVoiceRef = useRef(null);
  const blastAmbientRef = useRef(null);
  const hasPlayedRef = useRef(false); // Prevent double-play in React StrictMode
  
  // Mute/unmute audio when button clicked
  useEffect(() => {
    if (blastAmbientRef.current) {
      blastAmbientRef.current.muted = isMusicMuted;
    }
  }, [isMusicMuted]);

  useEffect(() => {
    // Guard against React StrictMode double-mounting in development
    if (hasPlayedRef.current) {
      return;
    }
    hasPlayedRef.current = true;

    // Play nuclear blast ambient sound at maximum volume (100% - browser limit)
    // This plays during the video
    const blastAmbient = new Audio('/assets/audio/nuclear-blast-ambient.mp3');
    blastAmbient.volume = 1.0; // Maximum browser volume (100%)
    blastAmbientRef.current = blastAmbient;
    blastAmbient.play().catch(e => console.log('Blast ambient play failed:', e));
    console.log('🎵 Playing nuclear blast ambient during intro video');

    // Show logo badge after 6 seconds (let video play first)
    setTimeout(() => {
      setShowTitle(true);
    }, 6000);

    // Auto-complete after 10 seconds (badge continues on map)
    const autoCompleteTimer = setTimeout(() => {
      onComplete();
    }, 10000);

    // Cleanup
    return () => {
      clearTimeout(autoCompleteTimer);
      if (welcomeVoiceRef.current) {
        welcomeVoiceRef.current.pause();
        welcomeVoiceRef.current.currentTime = 0;
      }
      if (blastAmbientRef.current) {
        blastAmbientRef.current.pause();
        blastAmbientRef.current.currentTime = 0;
      }
    };
  }, [onComplete]);

  const handleVideoEnd = () => {
    console.log('🎬 Intro video ended - now playing welcome voice');
    
    // Play welcome voice AFTER video ends
    const welcomeVoice = new Audio('/assets/audio/welcome-voice.mp3');
    welcomeVoice.volume = 0.8;
    welcomeVoiceRef.current = welcomeVoice;
    welcomeVoice.play().catch(e => console.log('Welcome voice play failed:', e));
    
    // Wait for welcome voice to finish (or max 3 seconds) before transitioning
    const welcomeVoiceDuration = 3000; // Adjust based on actual audio length
    setTimeout(() => {
      onComplete();
    }, welcomeVoiceDuration);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Background Video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        playsInline
        onEnded={handleVideoEnd}
      >
        <source src="/assets/intro-video.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay for better text visibility */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Animated Logo Badge */}
      {showTitle && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="logo-fly-in">
            <img 
              src="/assets/gtnw3-logo.png" 
              alt="GTNW3 - Global Thermonuclear War"
              className="max-w-3xl w-full h-auto px-8"
              style={{
                filter: `
                  drop-shadow(0 0 30px rgba(255, 215, 0, 0.8))
                  drop-shadow(0 0 60px rgba(255, 69, 0, 0.6))
                  drop-shadow(0 4px 12px rgba(0, 0, 0, 0.9))
                `,
              }}
            />
          </div>
        </div>
      )}

      {/* Skip button - Bottom center */}
      <button
        onClick={onComplete}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 bg-red-600/80 hover:bg-red-500 text-white font-bold uppercase tracking-wider rounded-lg transition-all border-2 border-red-400"
        style={{
          fontFamily: "'Orbitron', sans-serif",
          textShadow: '0 2px 4px rgba(0,0,0,0.8)'
        }}
      >
        Skip Intro »
      </button>
      
      {/* Music Mute Button - Top Right (same as world map) */}
      <button
        onClick={() => setIsMusicMuted(!isMusicMuted)}
        className="absolute top-8 right-8 w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 z-50"
        style={{
          background: isMusicMuted 
            ? 'linear-gradient(135deg, #6b7280, #4b5563)' 
            : 'linear-gradient(135deg, #10b981, #059669)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
        title={isMusicMuted ? "Unmute Music" : "Mute Music"}
      >
        <span className="text-2xl">
          {isMusicMuted ? '🔇' : '🔊'}
        </span>
      </button>

      <style jsx>{`
        @keyframes fly-in-from-distance {
          0% {
            transform: scale(0.1) translateZ(-1000px) rotateX(30deg);
            opacity: 0;
          }
          60% {
            transform: scale(1.1) translateZ(0) rotateX(0deg);
            opacity: 1;
          }
          80% {
            transform: scale(0.95) translateZ(0) rotateX(0deg);
          }
          100% {
            transform: scale(1) translateZ(0) rotateX(0deg);
            opacity: 1;
          }
        }

        .logo-fly-in {
          animation: fly-in-from-distance 2s ease-out forwards;
          transform-origin: center center;
          perspective: 1000px;
        }

        .logo-fly-in img {
          animation: glow-pulse 3s ease-in-out infinite;
        }

        @keyframes glow-pulse {
          0%, 100% {
            filter: brightness(1) drop-shadow(0 0 10px rgba(255, 215, 0, 0.6));
          }
          50% {
            filter: brightness(1.2) drop-shadow(0 0 20px rgba(255, 69, 0, 0.8));
          }
        }
      `}</style>
    </div>
  );
}
